import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { listCompanies } from '@/api/companies'
import { listUsers } from '@/api/users'
import { listRatesK1 } from '@/api/tariffs'

export type BillingOutcome =
  | 'all_terminal'
  | 'COMPLETED'
  | 'REFUSED_BY_INSPECTOR'
  | 'REFUSED_BY_SELLER'
  | 'REFUSED_BUYOUT'
  | 'CANCELLED'

export interface BillingFilterState {
  dateFrom: string
  dateTo: string
  companyId: string
  inspectorId: string
  city: string
  outcome: BillingOutcome
}

export function BillingFilters({
  value,
  onChange,
  onApply,
  onReset,
}: {
  value: BillingFilterState
  onChange: (v: BillingFilterState) => void
  onApply: () => void
  onReset: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)

  const outcomeOptions: { value: BillingOutcome; label: string }[] = [
    { value: 'all_terminal', label: t('billing.outcome.allTerminal') },
    { value: 'COMPLETED', label: t('billing.outcome.completed') },
    { value: 'REFUSED_BY_INSPECTOR', label: t('billing.outcome.refusedInspector') },
    { value: 'REFUSED_BY_SELLER', label: t('billing.outcome.refusedSeller') },
    { value: 'REFUSED_BUYOUT', label: t('billing.outcome.refusedBuyout') },
    { value: 'CANCELLED', label: t('billing.outcome.cancelled') },
  ]

  const { data: companies } = useQuery({
    queryKey: ['billing-filter-companies'],
    queryFn: () => listCompanies({ pageSize: 200, type: 'customer' }),
    staleTime: 60_000,
  })
  const { data: inspectors } = useQuery({
    queryKey: ['billing-filter-inspectors'],
    queryFn: () => listUsers({ pageSize: 200, role: 'inspector' }),
    staleTime: 60_000,
  })
  const { data: cities } = useQuery({
    queryKey: ['billing-filter-cities'],
    queryFn: listRatesK1,
    staleTime: 60_000,
  })

  const companyOptions = (companies?.items ?? []).map((c) => ({
    value: c.id,
    label: c.name,
  }))
  const inspectorOptions = (inspectors?.items ?? []).map((u) => ({
    value: u.id,
    label: u.full_name,
  }))
  const cityOptions = (cities ?? [])
    .filter((r) => r.is_active)
    .map((r) => ({ value: r.city, label: r.city }))

  const activeCount =
    (value.dateFrom || value.dateTo ? 1 : 0) +
    (value.companyId ? 1 : 0) +
    (value.inspectorId ? 1 : 0) +
    (value.city ? 1 : 0) +
    (value.outcome !== 'all_terminal' ? 1 : 0)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen((o) => !o)}
        >
          {t('billing.filters')}
          {activeCount ? (
            <span className="rounded-[var(--stryzh-radius-pill)] bg-[color:var(--stryzh-color-accent-orange)] px-2 py-0.5 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </div>
      {open ? (
        <div className="space-y-3 rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] px-3 py-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <Field label={t('billing.filter.period')}>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="h-9 min-w-0 flex-1 rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                  aria-label={t('billing.filter.dateFrom')}
                  value={value.dateFrom}
                  onChange={(e) =>
                    onChange({ ...value, dateFrom: e.target.value })
                  }
                />
                <span className="text-[var(--stryzh-color-text-secondary)]">
                  –
                </span>
                <input
                  type="date"
                  className="h-9 min-w-0 flex-1 rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                  aria-label={t('billing.filter.dateTo')}
                  value={value.dateTo}
                  onChange={(e) =>
                    onChange({ ...value, dateTo: e.target.value })
                  }
                />
              </div>
            </Field>
            <Field label={t('billing.filter.outcome')}>
              <Select<BillingOutcome>
                value={value.outcome}
                onChange={(v) =>
                  onChange({
                    ...value,
                    outcome: (v || 'all_terminal') as BillingOutcome,
                  })
                }
                options={outcomeOptions}
              />
            </Field>
            <Field label={t('billing.filter.company')}>
              <Select<string>
                value={value.companyId}
                onChange={(v) => onChange({ ...value, companyId: v })}
                options={companyOptions}
                emptyLabel={t('billing.filter.allOption')}
              />
            </Field>
            <Field label={t('billing.filter.inspector')}>
              <Select<string>
                value={value.inspectorId}
                onChange={(v) => onChange({ ...value, inspectorId: v })}
                options={inspectorOptions}
                emptyLabel={t('billing.filter.allOption')}
              />
            </Field>
            <Field label={t('billing.filter.city')}>
              <Select<string>
                value={value.city}
                onChange={(v) => onChange({ ...value, city: v })}
                options={cityOptions}
                emptyLabel={t('billing.filter.allOption')}
              />
            </Field>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-[var(--stryzh-color-border-default)] pt-3">
            <Button type="button" variant="ghost" onClick={onReset}>
              {t('common.reset')}
            </Button>
            <Button type="button" variant="primary" onClick={onApply}>
              {t('common.apply')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </span>
      {children}
    </label>
  )
}
